import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@sauda/ui/kit';
import { formatRupees } from '../../lib/money.js';

/** A number that climbs to its total in 600 ms, or shows the total at once for people who ask for less motion. */
export function CountUp({ to, ms = 600 }: { to: number; ms?: number }) {
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [value, setValue] = useState(reduce ? to : 0);
  useEffect(() => {
    if (reduce) {
      setValue(to);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      setValue(Math.round(to * (1 - (1 - p) ** 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, ms, reduce]);
  return <>{formatRupees(value)}</>;
}

/** The deal closed: a soft burst behind the brokerage received, then the title. No sound, no confetti. */
export function ClosedMoment({
  receivedPaise,
  onBack,
  renewalLine,
}: {
  receivedPaise: number;
  onBack: () => void;
  renewalLine: string | null;
}) {
  const { t } = useTranslation();
  return (
    <section
      className="closed-moment"
      aria-label={t('deal.closedTitle')}
      data-testid="deal-closed-moment"
    >
      <div className="closed-moment__burst deal-moment" aria-hidden="true" />
      <p className="k-t-label k-muted closed-moment__label">{t('deal.brokerageReceived')}</p>
      <p className="k-t-display-xl closed-moment__amount">
        <CountUp to={receivedPaise} />
      </p>
      <h2 className="k-t-headline closed-moment__title">{t('deal.closedTitle')}</h2>
      {renewalLine === null ? null : <p className="k-t-body k-muted">{renewalLine}</p>}
      <Button variant="tonal" size="lg" onClick={onBack}>
        {t('deal.backToDeals')}
      </Button>
    </section>
  );
}
